// Templates for generating realistic demo content

export interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  dateOfBirth?: string;
  age?: number;
}

export interface TaskTemplate {
  title: string;
  description?: string;
  daysFromNow: number;
  assignedToRole?: 'parent' | 'child' | 'any';
  listName: string;
}

export interface EventTemplate {
  title: string;
  description?: string;
  daysFromNow: number;
  duration: number; // in hours
  type: 'personal' | 'family' | 'appointment' | 'holiday';
  requiresChildren?: boolean;
}

export interface PersonTemplate {
  firstName: string;
  lastName: string;
  category: string;
  relationship: string;
  phone?: string;
  email?: string;
}

export interface DocumentTemplate {
  title: string;
  content: string;
  category: string;
  requiresChildren?: boolean;
}

// Task list templates
export const getTaskListTemplates = (hasChildren: boolean): { name: string; icon: string }[] => {
  const lists = [
    { name: 'Courses hebdomadaires', icon: '🛒' },
    { name: 'Tâches de la maison', icon: '🏠' },
  ];
  
  if (hasChildren) {
    lists.push({ name: 'École et devoirs', icon: '🎓' });
  }
  
  lists.push({ name: 'À faire cette semaine', icon: '📝' });
  
  return lists;
};

// Task templates
export const getTaskTemplates = (hasChildren: boolean): TaskTemplate[] => {
  const tasks: TaskTemplate[] = [
    // Courses
    { title: 'Faire les courses du week-end', daysFromNow: 2, listName: 'Courses hebdomadaires', assignedToRole: 'parent' },
    { title: 'Acheter du lait et des œufs', daysFromNow: 0, listName: 'Courses hebdomadaires', assignedToRole: 'any' },
    { title: 'Commander le vin pour le dîner', daysFromNow: 5, listName: 'Courses hebdomadaires', assignedToRole: 'parent' },
    
    // Maison
    { title: 'Sortir les poubelles', daysFromNow: 1, listName: 'Tâches de la maison', assignedToRole: 'any' },
    { title: 'Nettoyer la cuisine', daysFromNow: 0, listName: 'Tâches de la maison', assignedToRole: 'any' },
    { title: 'Passer l\'aspirateur', daysFromNow: 3, listName: 'Tâches de la maison', assignedToRole: 'any' },
    { title: 'Laver le linge', daysFromNow: 1, listName: 'Tâches de la maison', assignedToRole: 'parent' },
    { title: 'Arroser les plantes', daysFromNow: 2, listName: 'Tâches de la maison', assignedToRole: 'any' },
    
    // Cette semaine
    { title: 'Préparer le dîner de samedi', daysFromNow: 4, listName: 'À faire cette semaine', assignedToRole: 'parent' },
    { title: 'Appeler le dentiste', daysFromNow: 7, listName: 'À faire cette semaine', assignedToRole: 'parent' },
    { title: 'Réserver le restaurant', daysFromNow: 6, listName: 'À faire cette semaine', assignedToRole: 'parent' },
  ];
  
  if (hasChildren) {
    tasks.push(
      { title: 'Faire les devoirs de maths', daysFromNow: 0, listName: 'École et devoirs', assignedToRole: 'child' },
      { title: 'Préparer le cartable', daysFromNow: 1, listName: 'École et devoirs', assignedToRole: 'child' },
      { title: 'Lire 20 minutes', daysFromNow: 0, listName: 'École et devoirs', assignedToRole: 'child' },
      { title: 'Signer le carnet de liaison', daysFromNow: 2, listName: 'École et devoirs', assignedToRole: 'parent' },
    );
  }
  
  return tasks;
};

// Event templates
export const getEventTemplates = (hasChildren: boolean): EventTemplate[] => {
  const events: EventTemplate[] = [
    // Events proches
    { title: 'Bienvenue dans votre Clan', description: 'Découvrez votre organisateur familial', daysFromNow: 0, duration: 1, type: 'family' },
    { title: 'Apéro entre amis', description: 'Chez Sophie', daysFromNow: 2, duration: 3, type: 'family' },
    
    // Events à venir
    { title: 'Match de foot', daysFromNow: 6, duration: 2, type: 'personal' },
    { title: 'Sortie au cinéma', daysFromNow: 8, duration: 3, type: 'family' },
    { title: 'Dîner en famille', description: 'Restaurant italien', daysFromNow: 12, duration: 2, type: 'family' },
    { title: 'Pique-nique au parc', daysFromNow: 15, duration: 4, type: 'family' },
    { title: 'RDV dentiste', daysFromNow: 20, duration: 1, type: 'appointment' },
    { title: 'Week-end à la campagne', description: 'Chez les grands-parents', daysFromNow: 25, duration: 48, type: 'family' },
  ];
  
  if (hasChildren) {
    events.push(
      { title: 'Goûter d\'anniversaire', description: 'Chez des amis', daysFromNow: 18, duration: 3, type: 'family', requiresChildren: true },
      { title: 'Réunion parents-profs', daysFromNow: 30, duration: 1, type: 'appointment', requiresChildren: true },
    );
  }
  
  return events;
};

// Person templates
export const getPersonTemplates = (familyLastName: string, hasChildren: boolean): PersonTemplate[] => {
  const persons: PersonTemplate[] = [
    { firstName: 'Marie', lastName: familyLastName, category: 'extended_family', relationship: 'grandmother', phone: '+33 6 12 34 56 78' },
    { firstName: 'Jean', lastName: familyLastName, category: 'extended_family', relationship: 'grandfather', phone: '+33 6 23 45 67 89' },
    { firstName: 'Sophie', lastName: 'Martin', category: 'friends', relationship: 'friend', phone: '+33 6 34 56 78 90', email: 'sophie.martin@email.com' },
    { firstName: 'Dr. Dupont', lastName: '', category: 'medical_other', relationship: 'doctor', phone: '+33 1 45 67 89 01' },
  ];
  
  if (hasChildren) {
    persons.push(
      { firstName: 'École Pasteur', lastName: '', category: 'school', relationship: 'other', phone: '+33 1 56 78 90 12', email: 'contact@ecolepasteur.fr' },
      { firstName: 'Claire', lastName: 'Dubois', category: 'services', relationship: 'other', phone: '+33 6 45 67 89 01', email: 'claire.dubois@email.com' },
    );
  }
  
  return persons;
};

// Document templates
export const getDocumentTemplates = (hasChildren: boolean): DocumentTemplate[] => {
  const documents: DocumentTemplate[] = [
    // 📝 NOTE 1 : Liste de courses
    {
      title: 'Liste de courses',
      category: 'household',
      content: `# Liste de courses de la semaine

## Fruits et légumes
- Pommes
- Bananes
- Tomates
- Salade

## Épicerie
- Pâtes
- Riz
- Huile d'olive

## Produits frais
- Lait
- Œufs
- Fromage`,
    },
    
    // 📝 NOTE 2 : Idées de sorties
    {
      title: 'Idées de sorties',
      category: 'other',
      content: `# Idées de sorties en famille

## Ce week-end
- Parc près de la maison
- Cinéma (nouveau film d'animation)
- Musée des sciences

## Prochaines vacances
- Week-end à la mer
- Visite des grands-parents
- Parc d'attractions`,
    },
    
    // 📁 DOSSIER 1 : Contacts médicaux
    {
      title: 'Contacts médicaux',
      category: 'health',
      content: `# Contacts médicaux importants

## Médecin traitant
Dr. Dupont
Tél : 01 45 67 89 01

## Pharmacie
Pharmacie Centrale
Tél : 01 45 67 89 02

## Urgences
SAMU : 15
Pompiers : 18`,
    },
    
    // 📁 DOSSIER 2 : Informations maison
    {
      title: 'Informations maison',
      category: 'household',
      content: `# Répertoire maison

## Artisans
- Plombier : 06 12 34 56 78
- Électricien : 06 23 45 67 89

## Services
- Gardien : 06 34 56 78 90
- Internet : support.sfr.fr

## Codes et accès
- Code portail : 1234A
- WiFi : maison2024`,
    },
    
    // 📁 DOSSIER 3 : Recettes favorites
    {
      title: 'Nos recettes préférées',
      category: 'recipes',
      content: `# Recettes de famille

## Plat du dimanche
Poulet rôti aux herbes
- 1 poulet fermier
- Herbes de Provence
- Citron

## Dessert facile
Gâteau au chocolat
- 200g chocolat
- 4 œufs
- 100g sucre
- 100g beurre

## Astuce
Toujours doubler les portions pour avoir des restes !`,
    },
  ];
  
  return documents;
};
